import { createApi } from "@reduxjs/toolkit/query/react";
import type { BaseQueryApi, BaseQueryFn } from "@reduxjs/toolkit/query";
import {
  gboClient,
  getApiFailureMessage,
  type ApiFailure,
  type GboRequestOptions,
} from "../lib/gboApiClient";
import { credentialsReceived, signedOut, type PortalCredentials } from "../features/auth/authSlice";
import { selectPortalCredentials } from "../features/auth/authSelectors";
import { portalIdentity } from "../features/auth/portalIdentity";
import { selectTenantId } from "../features/tenant/tenantSelectors";
import type { RootState } from "./store";

type GboQuery = Omit<GboRequestOptions, "tenantId" | "signal"> & {
  url: string;
} & (
    | { responseType?: "json"; fallbackFileName?: never }
    | { responseType: "file"; fallbackFileName: string }
  );

type RefreshResult = { data: PortalCredentials } | { error: ApiFailure };
const pendingRefreshes = new WeakMap<
  BaseQueryApi["dispatch"],
  {
    tenantId: string | undefined;
    credentials: PortalCredentials | null;
    promise: Promise<RefreshResult>;
  }
>();
const authChanged: ApiFailure = { message: "Your sign-in session changed. Please try again." };

async function getPortalCredentials(
  api: BaseQueryApi,
  tenantId: string | undefined,
  startingCredentials: PortalCredentials | null,
): Promise<RefreshResult> {
  if (!tenantId) return { error: { message: "Current site config is not loaded." } };

  try {
    const result = await gboClient.post<PortalCredentials>("auth/refresh", { tenantId });
    const state = api.getState() as RootState;
    if (
      selectTenantId(state) !== tenantId ||
      selectPortalCredentials(state) !== startingCredentials
    )
      return { error: authChanged };
    if ("error" in result) return result;
    api.dispatch(credentialsReceived(result.data));
    return { data: selectPortalCredentials(api.getState() as RootState)! };
  } catch (error) {
    return {
      error: {
        message: getApiFailureMessage(error, "Unable to refresh your login. Please try again."),
      },
    };
  }
}

export const portalAuth = {
  refreshCredentials(api: BaseQueryApi): Promise<RefreshResult> {
    const state = api.getState() as RootState;
    const tenantId = selectTenantId(state);
    const credentials = selectPortalCredentials(state);
    const pending = pendingRefreshes.get(api.dispatch);
    if (pending && pending.tenantId === tenantId && pending.credentials === credentials)
      return pending.promise;

    const promise = getPortalCredentials(api, tenantId, credentials).finally(() => {
      if (pendingRefreshes.get(api.dispatch)?.promise === promise)
        pendingRefreshes.delete(api.dispatch);
    });
    pendingRefreshes.set(api.dispatch, { tenantId, credentials, promise });
    return promise;
  },
};

const baseQuery: BaseQueryFn<GboQuery, unknown, ApiFailure> = async (
  { url, responseType, fallbackFileName, ...options },
  api,
) => {
  const state = api.getState() as RootState;
  const tenantId = selectTenantId(state);
  if (!tenantId) return { error: { message: "Current site config is not loaded." } };

  const sendRequest = (credentials: PortalCredentials | null) => {
    const headers = new Headers(options.headers);
    if (credentials) headers.set("Authorization", `Bearer ${credentials.accessToken}`);
    const request = {
      ...options,
      tenantId,
      signal: api.signal,
      headers: Object.fromEntries(headers.entries()),
    };
    return responseType === "file"
      ? gboClient.file(url, { ...request, fallbackFileName })
      : gboClient.request(url, request);
  };

  const complete = (result: Awaited<ReturnType<typeof sendRequest>>) => {
    if (responseType !== "file" || "error" in result) return result;
    const current = api.getState() as RootState;
    if (
      api.signal.aborted ||
      selectTenantId(current) !== tenantId ||
      !portalIdentity.isSameScope(selectPortalCredentials(state), selectPortalCredentials(current))
    )
      return { error: authChanged };
    return result;
  };

  try {
    let preparedCredentials = selectPortalCredentials(state);
    if (
      preparedCredentials &&
      Date.parse(preparedCredentials.accessExpiresAt) <= Date.now() + 30_000
    ) {
      const renewed = await portalAuth.refreshCredentials(api);
      if ("data" in renewed) preparedCredentials = renewed.data;
    }

    const preparedState = api.getState() as RootState;
    const sentCredentials = selectPortalCredentials(preparedState);
    if (
      selectTenantId(preparedState) !== tenantId ||
      !portalIdentity.isSameScope(preparedCredentials, sentCredentials)
    )
      return { error: authChanged };

    const result = await sendRequest(sentCredentials);
    if ("data" in result || result.error.status !== 401) return complete(result);
    const currentState = api.getState() as RootState;
    if (selectTenantId(currentState) !== tenantId) return result;
    const currentCredentials = selectPortalCredentials(currentState);
    if (currentCredentials !== sentCredentials) {
      if (!portalIdentity.isSameScope(sentCredentials, currentCredentials)) return result;
      return complete(await sendRequest(currentCredentials));
    }

    const renewed = await portalAuth.refreshCredentials(api);
    const retryState = api.getState() as RootState;
    const retryCredentials = selectPortalCredentials(retryState);
    if ("error" in renewed) {
      if (selectTenantId(retryState) === tenantId && retryCredentials === sentCredentials)
        api.dispatch(signedOut());
      return result;
    }

    if (
      selectTenantId(retryState) !== tenantId ||
      !portalIdentity.isSameScope(renewed.data, retryCredentials)
    )
      return { error: authChanged };

    return complete(await sendRequest(retryCredentials));
  } catch (error) {
    return {
      error: {
        message: getApiFailureMessage(error, "Unable to reach the server. Please try again."),
      },
    };
  }
};

export const gboApi = createApi({
  reducerPath: "gboApi",
  baseQuery,
  tagTypes: ["SiteConfig", "Invoice", "Account"],
  endpoints: () => ({}),
});
